import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
} from "react-hook-form";

import {
  ComboBox,
  ComboBoxButton,
  ComboBoxGroup,
  ComboBoxInput,
  ComboBoxListBox,
  ComboBoxListItem,
  ComboBoxPopover,
} from "@/components/combobox";
import { Label, LabeledGroup } from "@/components/field";
import { Text } from "@/components/text";
import { useResolveTimezones } from "@/features/users/hooks/useResolveTimezones";

type SelectTimezoneProps<T extends FieldValues> = {
  control: Control<T>;
  timezoneField: Path<T>;
};

export function SelectTimezone<T extends FieldValues>({
  control,
  timezoneField,
}: SelectTimezoneProps<T>) {
  const { data, isLoading } = useResolveTimezones();

  return (
    <Controller
      control={control}
      name={timezoneField}
      render={({ field, fieldState }) => (
        <LabeledGroup className="mt-4">
          <Label requiredHint>Timezone</Label>
          <ComboBox
            allowsEmptyCollection
            menuTrigger="input"
            onSelectionChange={(key) => field.onChange(key)}
            selectedKey={field.value}
          >
            <ComboBoxGroup>
              <ComboBoxInput
                placeholder={isLoading ? "Loading..." : "Select a timezone"}
              />
              <ComboBoxButton />
            </ComboBoxGroup>
            <ComboBoxPopover>
              <ComboBoxListBox
                renderEmptyState={() => (
                  <Text className="p-2">No results found</Text>
                )}
              >
                {data?.map((tz) => (
                  <ComboBoxListItem id={tz.id} key={tz.id}>
                    {tz.displayName}
                  </ComboBoxListItem>
                ))}
              </ComboBoxListBox>
            </ComboBoxPopover>
          </ComboBox>
          {fieldState.error && (
            <Text className="text-text-error-primary text-sm mt-1">
              {fieldState.error.message}
            </Text>
          )}
        </LabeledGroup>
      )}
    />
  );
}
